import { useState } from 'react';

import { format, isValid } from 'date-fns';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Checkbox,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from '@cfa/react-components';
import { DateValue, UNSTABLE_DatePicker as DatePicker } from '@cfa/react-core';

import { isValidYear } from '@/utils/date';

interface DueDatePopUpProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (date: string | null, id?: string) => void;
}

const DueDatePopUp: React.FC<DueDatePopUpProps> = ({
  isOpen,
  onClose,
  onSave,
}) => {
  const { t } = useTranslation();
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [checkboxClicked, setCheckboxClicked] = useState(false);
  const [disableDatePicker, setDisableDatePicker] = useState(false);

  const [prevIsOpen, setPrevIsOpen] = useState(isOpen);
  if (prevIsOpen !== isOpen) {
    setPrevIsOpen(isOpen);
    setSelectedDate(null);
    setCheckboxClicked(false);
    setDisableDatePicker(false);
  }

  const onDateChange = (date: DateValue | null) => {
    if (!date) {
      setSelectedDate(null);
      return;
    }

    // we only receive a date from the Date Picker when there is at least 1 number typed into the year section, we check to make sure the year is 4 digits here
    if (isValidYear(date.year)) {
      const formattedDate = format(
        date.toDate(Intl.DateTimeFormat().resolvedOptions().timeZone),
        "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'",
      );
      // checks to make sure date is a proper date format
      if (isValid(new Date(formattedDate))) {
        setSelectedDate(formattedDate);
      }
    } else {
      setSelectedDate(null);
    }
  };

  const onCheckboxClick = () => {
    setCheckboxClicked(prev => !prev);
    setDisableDatePicker(prev => !prev);
  };

  return (
    <>
      <Modal
        data-testid="DueDatePopUp"
        onClose={onClose}
        show={isOpen}
        size="md"
      >
        <ModalHeader>
          {t('TrainingPlans.accountability.setDueDate')}
        </ModalHeader>
        <ModalBody>
          <div className="flex flex-row justify-center">
            <div className="flex flex-col items-start justify-start text-left">
              <DatePicker
                data-testid="DatePicker"
                isDisabled={disableDatePicker}
                isRequired={!checkboxClicked}
                isInvalid={checkboxClicked ? false : undefined}
                label={t('TrainingPlans.accountability.enterDueDate')}
                onChange={onDateChange}
                shouldForceLeadingZeros={false}
              />
              <Checkbox
                data-testid="DueDateCheckbox"
                label={t('TrainingPlans.accountability.due.noDueDate')}
                onChange={onCheckboxClick}
                className="mt-8!"
              />
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            data-testid="ButtonSave"
            disabled={!selectedDate && !checkboxClicked}
            id="SaveDueDate"
            onClick={() => onSave(checkboxClicked ? null : selectedDate)}
          >
            {t('Button.save')}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default DueDatePopUp;
